// Shared by Cloud Functions and the frontend (imported there as @shared).
// Game plans: a manager's usual strategy and per-game changes for the next
// few games. Stored in leagues/{id}/plans/{teamId}, which only that team's
// manager can read, so an opponent can't see a plan before tip-off.
//
// plans: {usual: {offense, defense, pace, depth},
//   games: {gameId: {offense, defense, pace, keyOn}}, ts}
//
// keyOn: an opponent's player the defense focuses on (engine TUNING.keyOn).
// Computer coaches pick one with autoKeyOn, from public information only.

import {cleanStrategy, DEFENSES, OFFENSES, PACES} from "./strategy.js";

// Plans can be set this many days of games ahead (times the league's
// game times per day: 3 games in college, about 6 in pro).
export const PLAN_DAYS = 3;
// A team counts as built around one player when that player's share of the
// scoring is at least this many times the next player's.
export const KEY_RELIANCE = 1.35;
// Chance a computer coach keys on such a player, and on the top scorer of
// a balanced team (now and then, to keep scouting honest).
export const KEY_CHANCE = {reliant: 0.8, balanced: 0.1};
// Season games before scoring is judged from points rather than ratings.
const KEY_MIN_GAMES = 4;

const GAME_KEYS = [
  ["offense", OFFENSES], ["defense", DEFENSES], ["pace", PACES],
];

/**
 * The team's games a manager can plan for now: not yet played, after the
 * current day, the next `count` of them in schedule order.
 * @param {object[]} games summary/schedule games
 * @param {string} teamId
 * @param {number} day the league's current day (games after it are ahead)
 * @param {number} count how many games
 * @return {object[]} schedule entries
 */
export function upcomingGames(games, teamId, day, count) {
  return (games ?? [])
      .filter((g) => (g.home === teamId || g.away === teamId) &&
        !g.result && g.day > day)
      .sort((a, b) => a.day - b.day)
      .slice(0, count);
}

/**
 * How many games ahead plans reach in a league: PLAN_DAYS days of game
 * times.
 * @param {object} league
 * @return {number}
 */
export function planReach(league) {
  return PLAN_DAYS * Math.max(1, league?.automation?.times?.length ??
    (league?.type === "pro" ? 2 : 1));
}

/**
 * Checks one game's plan. Throws an Error with a message for the manager
 * when something is wrong.
 * @param {object} plan {offense, defense, pace, keyOn?}
 * @param {object} oppPlayers the opponent's roster {id: player}
 * @return {object} {offense, defense, pace, keyOn, keyOnName}: the name
 *   is for showing the plan without loading the opponent
 */
export function cleanGamePlan(plan, oppPlayers) {
  const out = {};
  for (const [key, list] of GAME_KEYS) {
    if (!list.some((x) => x.key === plan?.[key])) {
      throw new Error(`Unknown ${key}: ${plan?.[key]}.`);
    }
    out[key] = plan[key];
  }
  const keyOn = plan.keyOn || null;
  if (keyOn && !oppPlayers?.[keyOn]) {
    throw new Error("That player isn't on the other team.");
  }
  out.keyOn = keyOn;
  out.keyOnName = keyOn ?
    `${oppPlayers[keyOn].fName ?? ""} ${oppPlayers[keyOn].lName ?? ""}`
        .trim() : null;
  return out;
}

/**
 * The strategy a team runs in one game: its usual strategy with that
 * game's plan on top.
 * @param {object|null} plans the team's plans document
 * @param {string} gameId
 * @param {object} [fallback] used when there is no usual strategy saved
 *   (teams from before plans: their team.strategy)
 * @return {object} {offense, defense, pace, depth, keyOn}
 */
export function planStrategy(plans, gameId, fallback) {
  const usual = cleanStrategy(plans?.usual ?? fallback);
  const game = plans?.games?.[gameId];
  if (!game) return {...usual, keyOn: null};
  return {...usual, offense: game.offense ?? usual.offense,
    defense: game.defense ?? usual.defense, pace: game.pace ?? usual.pace,
    keyOn: game.keyOn ?? null};
}

/**
 * Each player's share of the team's scoring: season points once the team
 * has played a few games, else a scoring estimate from visible ratings.
 * Healthy players only.
 * @param {object} team team document (visible fields)
 * @return {object[]} [{id, share}] highest first
 */
export function scoringShares(team) {
  const healthy = Object.values(team.players ?? {})
      .filter((p) => !(p.injury?.gamesOut > 0));
  const played = (team.teamStats?.gp ?? 0) >= KEY_MIN_GAMES;
  // Before then: the likely rotation (top eight by overall), weighted as
  // the engine picks shooters.
  const pool = played ? healthy : [...healthy]
      .sort((a, b) => b.overall - a.overall).slice(0, 8);
  const raw = pool.map((p) => ({id: p.id, v: played ?
    (p.season?.pts ?? 0) :
    ((0.3 * p.off_3pt + 0.3 * p.off_shooting + 0.4 * p.off_drive) / 50) **
      2}));
  const total = raw.reduce((s, x) => s + x.v, 0) || 1;
  return raw.map((x) => ({id: x.id, share: x.v / total}))
      .sort((a, b) => b.share - a.share || a.id.localeCompare(b.id));
}

/**
 * How much a team leans on its top scorer: the top share over the next.
 * @param {object} team
 * @return {object} {id, reliance} (id null for an empty roster)
 */
export function topScorer(team) {
  const [first, second] = scoringShares(team);
  if (!first) return {id: null, reliance: 0};
  return {id: first.id,
    reliance: second?.share ? first.share / second.share : 9};
}

/**
 * A computer coach's key-on choice against an opponent: its top scorer
 * when the opponent leans on that player (most of the time), rarely
 * otherwise. Public information only.
 * @param {object} opp the opponent's team document
 * @param {object} rng seeded
 * @return {string|null} player id
 */
export function autoKeyOn(opp, rng) {
  const {id, reliance} = topScorer(opp);
  if (!id) return null;
  const chance = reliance >= KEY_RELIANCE ? KEY_CHANCE.reliant :
    KEY_CHANCE.balanced;
  return rng.chance(chance) ? id : null;
}

/**
 * Plans for games still ahead only (drops games already played or no
 * longer the team's).
 * @param {object} games {gameId: plan}
 * @param {Set<string>} keep game ids still ahead
 * @return {object}
 */
export function prunePlans(games, keep) {
  return Object.fromEntries(Object.entries(games ?? {})
      .filter(([id]) => keep.has(id)));
}
